import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, Observable, skipUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DashboardOutletDirective } from '../../directives/dashboard-outlet.directive';
import { CardComponent } from '../cards/card.component';
import { dashboardCards } from '../cards/cards';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { SwayTenant } from 'projects/angular-clarity/src/app/stores/client/sway/tenant/model';
import { loadSwayTenant } from 'projects/angular-clarity/src/app/stores/client/sway/tenant/actions';

export interface DashItem {
    title: string;
    id: string;
    component: CardComponent;
    cols: number;
    rows: number;
}

interface CardsLayout {
    columns: number;
    items: DashItem[];
}

@Component({
    selector: 'app-container',
    templateUrl: './container.component.html',
    styleUrls: ['./container.component.scss'],
})
export class ContainerComponent implements OnInit {
    @ViewChildren(DashboardOutletDirective) dashboardOutlet: QueryList<DashboardOutletDirective>;
    public tenantId = this.route.snapshot.paramMap.get('tenant');
    tenant$: Observable<SwayTenant>;
    public cardsLayout$: BehaviorSubject<CardsLayout> = new BehaviorSubject({ columns: 12, items: default_dashboard });
    public showOverlay$: BehaviorSubject<boolean> = new BehaviorSubject(false); // overlay wrapper

    constructor(
        private breakpointObserver: BreakpointObserver,
        private cd: ChangeDetectorRef,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store<any>,
    ) {}

    ngOnInit() {
        // this.cd.detectChanges();
        // this.loadContents();
        this.breakpointSubscription();

        this.store.dispatch(loadSwayTenant({ _tenant: this.tenantId }));

        this.tenant$ = this.store
            .select(client(this.tenantId).sway.tenant.item)
            .pipe(skipUntil(this.store.select(client(this.tenantId).sway.tenant.loaded)));
    }

    breakpointSubscription() {
        /** Based on the screen size, switch from standard to one column per row */
        this.breakpointObserver
            .observe([
                Breakpoints.XSmall,
                Breakpoints.Small,
                Breakpoints.HandsetPortrait,
                Breakpoints.Medium,
                Breakpoints.Large,
                Breakpoints.XLarge,
            ])
            .subscribe((state: BreakpointState) => {
                if (
                    state.breakpoints[Breakpoints.Small] === true ||
                    state.breakpoints[Breakpoints.Medium] === true ||
                    state.breakpoints[Breakpoints.HandsetPortrait] === true
                ) {
                    this.cardsLayout$.next({
                        items: small_dashboard,
                        columns: 12,
                    });
                } else if (state.breakpoints[Breakpoints.Large] === true) {
                    this.cardsLayout$.next({
                        items: medium_dashboard,
                        columns: 12,
                    });
                } else {
                    this.cardsLayout$.next({
                        items: default_dashboard,
                        columns: 12,
                    });
                }

                this.cd.detectChanges();
            });
    }

    navigateToBlockedAccounts(tenantId: string) {
        this.router.navigate(['/client', tenantId, 'hardening', 'blocked_accounts']);
    }
}

const default_dashboard: DashItem[] = [
    { title: 'Monitoring', id: '00', component: dashboardCards.monitoring, cols: 12, rows: 1 },
    { title: 'Baselines', id: '02', component: dashboardCards.sway, cols: 4, rows: 1 },
    { title: 'Baseline Categories', id: '10', component: dashboardCards.baselineCategories, cols: 8, rows: 1 },
    { title: 'Mail Flow', id: '03', component: dashboardCards.mailFlow, cols: 4, rows: 1 },
    { title: 'Accounts Risks / Management', id: '04', component: dashboardCards.risks, cols: 8, rows: 1 },
    { title: 'Remote Access', id: '05', component: dashboardCards.remoteAccess, cols: 2, rows: 1 },
    { title: 'App Report', id: '06', component: dashboardCards.appReport, cols: 2, rows: 1 },
    { title: 'Pwned Check', id: '08', component: dashboardCards.pwnedCheck, cols: 2, rows: 1 },
    { title: 'Change History', id: '09', component: dashboardCards.changesHistory, cols: 2, rows: 1 },
    { title: 'Blocked Accounts', id: '09', component: dashboardCards.blockedAccounts, cols: 2, rows: 1 },
];
const medium_dashboard: DashItem[] = [
    { title: 'Monitoring', id: '00', component: dashboardCards.monitoring, cols: 12, rows: 1 },
    { title: 'Baselines', id: '02', component: dashboardCards.sway, cols: 5, rows: 1 },
    { title: 'Accounts Risks / Management', id: '04', component: dashboardCards.risks, cols: 7, rows: 1 },
    { title: 'Baseline Categories', id: '10', component: dashboardCards.baselineCategories, cols: 10, rows: 1 },
    { title: 'Remote Access', id: '03', component: dashboardCards.remoteAccess, cols: 2, rows: 1 },
    { title: 'Mail Flow', id: '06', component: dashboardCards.mailFlow, cols: 4, rows: 1 },
    { title: 'Pwned Check', id: '08', component: dashboardCards.pwnedCheck, cols: 2, rows: 1 },
    { title: 'Change History', id: '09', component: dashboardCards.changesHistory, cols: 2, rows: 1 },
    { title: 'App Report', id: '05', component: dashboardCards.appReport, cols: 2, rows: 1 },
    { title: 'Blocked Accounts', id: '09', component: dashboardCards.blockedAccounts, cols: 2, rows: 1 },
];

const small_dashboard: DashItem[] = [
    { title: 'Monitoring', id: '00', component: dashboardCards.monitoring, cols: 12, rows: 1 },
    { title: 'Baselines', id: '02', component: dashboardCards.sway, cols: 4, rows: 3 },
    { title: 'Accounts Risks / Management', id: '04', component: dashboardCards.risks, cols: 8, rows: 1 },
    { title: 'Baseline Categories', id: '10', component: dashboardCards.baselineCategories, cols: 12, rows: 1 },
    { title: 'Remote Access', id: '03', component: dashboardCards.remoteAccess, cols: 3, rows: 1 },
    { title: 'Mail Flow', id: '06', component: dashboardCards.mailFlow, cols: 6, rows: 1 },
    { title: 'Pwned Check', id: '08', component: dashboardCards.pwnedCheck, cols: 3, rows: 1 },
    { title: 'Change History', id: '09', component: dashboardCards.changesHistory, cols: 3, rows: 1 },
    { title: 'App Report', id: '05', component: dashboardCards.appReport, cols: 3, rows: 1 },
    { title: 'Blocked Accounts', id: '09', component: dashboardCards.blockedAccounts, cols: 3, rows: 1 },
];
